import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { Play, X } from 'lucide-react';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { IProject } from '../../types/global/helper';
import { useDispatch } from 'react-redux';
import { setmedia } from '../../redux/project/actions';
import { COMPANY_BRAND_ORANGE } from '../../constants/companyBrand';

interface ICompanyVideoThumbnails {
    videos?: IProject[];
    isEditMode?: boolean;
    onRemoveVideo?: (videoId: string) => void;
}

const CompanyVideoThumbnails: React.FC<ICompanyVideoThumbnails> = ({
    videos = [],
    isEditMode = false,
    onRemoveVideo,
}) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();

    if (!videos || videos.length === 0) {
        return null;
    }

    const handleVideoClick = (video: IProject) => {
        dispatch(
            setmedia({
                media: video,
                mediaType: 'video',
            }),
        );
    };

    return (
        <Box sx={{ mb: 3 }}>
            <Typography
                component="h2"
                sx={{
                    color: COMPANY_BRAND_ORANGE,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: '0.6875rem',
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase',
                    mb: 2,
                }}>
                Company Videos
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'auto',
                    pb: 1,
                    '&::-webkit-scrollbar': {
                        height: 6,
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: theme[themeStyle].deep_grey,
                        borderRadius: 3,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme[themeStyle].tag_white_light,
                        borderRadius: 3,
                        '&:hover': {
                            backgroundColor: theme[themeStyle].tag_white,
                        },
                    },
                }}>
                {videos.map((video, index) => (
                    <Box
                        key={video._id || index}
                        onClick={() => handleVideoClick(video)}
                        sx={{
                            position: 'relative',
                            minWidth: 200,
                            height: 120,
                            borderRadius: 2,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            backgroundColor: theme[themeStyle].deep_grey,
                            border: `1px solid ${theme[themeStyle].tag_white_light}`,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                transform: 'scale(1.02)',
                                borderColor: theme[themeStyle].tag_white,
                            },
                        }}>
                        {/* Remove Button */}
                        {isEditMode && onRemoveVideo && (
                            <IconButton
                                onClick={e => {
                                    e.stopPropagation();
                                    onRemoveVideo(video._id);
                                }}
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    backgroundColor: 'rgba(0,0,0,0.7)',
                                    color: theme[themeStyle].tag_white,
                                    width: 32,
                                    height: 32,
                                    zIndex: 3,
                                    '&:hover': {
                                        backgroundColor: 'rgba(0,0,0,0.9)',
                                    },
                                }}>
                                <X size={16} />
                            </IconButton>
                        )}

                        {/* Video Thumbnail */}
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                backgroundImage: `url(${video.thumbnail || video.feature_image})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                            {/* Play Button Overlay */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'background-color 0.2s ease-in-out',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    },
                                }}>
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            backgroundColor: 'white',
                                            transform: 'scale(1.1)',
                                        },
                                    }}>
                                    <Play
                                        size={16}
                                        color={theme[themeStyle].pure_black}
                                        fill={theme[themeStyle].pure_black}
                                    />
                                </Box>
                            </Box>
                        </Box>

                        {/* Video Title Overlay */}
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.8))',
                                p: 1,
                            }}>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '0.75rem',
                                    lineHeight: 1.2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                }}>
                                {video.title}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export { CompanyVideoThumbnails };
